import { IAIComparisonProcessingStatus, PolicyType, TaskStatus } from "@/api";
import {
    getAIComparisonsForChart,
    getAIComparisonStatus,
    getCompareInChart,
} from "@/api/compare-in-chart/compare-in-chart";
import { FrontendPageContent } from "@/components/frontend/FrontendPageContent";
import { PageHeader } from "@/components/shared/layout/PageHeader";
import { SubHeader } from "@/components/shared/layout/SubHeader";
import { HOME_ORG_PATH, TASK_REFETCH_INTERVAL } from "@/config/constants";
import { useBreadcrumb } from "@/contexts/shared/breadcrumb";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { comparisonHasFields } from "@/utils/comparison";
import { CircularProgress } from "@mui/material";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import React, { useCallback } from "react";
import { useParams } from "react-router";
import { ClausesList } from "./components/chart/ClausesList";
import { FieldsList } from "./components/chart/FieldsList";
import FilterBar from "./components/chart/FilterBar";
import { PageHeaderActions } from "./components/chart/PageHeaderActions";
import { PageHeaderLeftContent } from "./components/chart/PageHeaderLeftContent";

const Chart = () => {
    const { chartId } = useParams();

    useDocumentTitle("Comparison");

    const [coverageFilter, setCoverageFilter] = React.useState("all");
    const [clauseTypeFilter, setClauseTypeFilter] = React.useState("all");
    const [searchValue, setSearchValue] = React.useState("");
    const [expandedClauseIds, setExpandedClauseIds] = React.useState<
        Set<number>
    >(new Set());
    const [expandedPolicyTypes, setExpandedPolicyTypes] = React.useState<
        Set<PolicyType>
    >(new Set());

    const [aiComparisonsState, setAIComparisonsState] =
        React.useState<IAIComparisonProcessingStatus | null>(null);
    const [viewMode, setViewMode] = React.useState<"clauses" | "fields">(
        "clauses"
    );

    const { setBreadcrumbs } = useBreadcrumb();

    const { data, isPending, isError, error } = useQuery({
        queryKey: ["frontend-chart", chartId],
        queryFn: () => getCompareInChart(chartId!),
        enabled: !!chartId,
    });

    // Query to get AI comparisons for the chart - refetch when aiComparisonsState changes
    const { data: aiComparisons } = useQuery({
        queryKey: ["frontend-ai-comparisons", chartId, aiComparisonsState],
        queryFn: () => getAIComparisonsForChart(chartId!),
        enabled: !!chartId,
        placeholderData: keepPreviousData,
    });

    // Query to track the status of AI comparisons if any are in progress
    const { data: aiComparisonsStatus } = useQuery({
        queryKey: ["frontend-ai-comparisons-status", chartId],
        queryFn: () => getAIComparisonStatus(chartId!),
        enabled:
            !!chartId &&
            !!aiComparisons &&
            aiComparisons.find(
                (c) =>
                    c.status === TaskStatus.IN_PROGRESS ||
                    c.status === TaskStatus.PENDING
            ) !== undefined,
        refetchInterval: TASK_REFETCH_INTERVAL,
    });

    const expandAllClauses = useCallback(() => {
        if (!data) return;

        const clauseIds = new Set<number>();
        data.scopes.forEach((scope) => {
            scope.scope_clauses.forEach((scopeClause) => {
                clauseIds.add(scopeClause.clause.id);
            });
        });
        setExpandedClauseIds(clauseIds);
    }, [data]);

    const expandAllPolicyExtractions = useCallback(() => {
        if (!data) return;

        const policyTypes = new Set<PolicyType>();
        data.scopes.forEach((scope) => {
            scope.extracted_fields_v2?.forEach((fieldGroup) => {
                if (fieldGroup.scalar_fields.length > 0) {
                    policyTypes.add(fieldGroup.policy_type as PolicyType);
                }
            });
        });
        setExpandedPolicyTypes(policyTypes);
    }, [data]);

    // Initialize all clauses as expanded when data loads
    React.useEffect(() => {
        if (data) {
            // Expand all clauses by default
            expandAllClauses();

            // Expand all policy extractions by default
            expandAllPolicyExtractions();
        }

        setBreadcrumbs([
            { label: "Home", path: HOME_ORG_PATH, openInNewTab: true },
            {
                label: "Comparisons",
                path: "/organizations/comparisons",
                openInNewTab: true,
            },
            { label: data?.name || "..." },
        ]);
    }, [data, expandAllClauses, expandAllPolicyExtractions, setBreadcrumbs]);

    // Use effect to refetch AI comparisons when status changes
    React.useEffect(() => {
        if (aiComparisonsStatus) {
            setAIComparisonsState(aiComparisonsStatus);
        }
    }, [aiComparisonsStatus]);

    if (isPending) {
        return <CircularProgress />;
    }

    if (isError) {
        throw error;
    }

    // Extract unique clause types from the data
    const getUniqueClauseTypes = () => {
        const clauseTypesSet = new Set<string>();
        data.scopes.forEach((scope) => {
            scope.scope_clauses.forEach((scopeClause) => {
                scopeClause.clause.clause_types?.forEach((type) => {
                    clauseTypesSet.add(type.name);
                });
            });
        });
        return Array.from(clauseTypesSet).sort();
    };

    const handleExpandAll = () => {
        switch (viewMode) {
            case "clauses":
                expandAllClauses();
                break;
            case "fields":
                expandAllPolicyExtractions();
                break;
        }
    };

    const handleCollapseAll = () => {
        switch (viewMode) {
            case "clauses":
                setExpandedClauseIds(new Set());
                break;
            case "fields":
                setExpandedPolicyTypes(new Set());
                break;
        }
    };

    const handleClauseToggle = (clauseId: number) => {
        setExpandedClauseIds((prev) => {
            const newSet = new Set(prev);
            if (newSet.has(clauseId)) {
                newSet.delete(clauseId);
            } else {
                newSet.add(clauseId);
            }
            return newSet;
        });
    };

    const handlePolicyTypeToggle = (policyType: PolicyType) => {
        setExpandedPolicyTypes((prev) => {
            const newSet = new Set(prev);
            if (newSet.has(policyType)) {
                newSet.delete(policyType);
            } else {
                newSet.add(policyType);
            }
            return newSet;
        });
    };

    const renderClauses = () => (
        <>
            <SubHeader subtitle="Disclaimer: AI-generated comparisons are provided for informational purposes only, do not constitute legal advice and are not a substitute for review from an insurance professional or attorney." />
            <ClausesList
                chart={data}
                aiComparisons={aiComparisons}
                searchValue={searchValue}
                coverageFilter={coverageFilter}
                clauseTypeFilter={clauseTypeFilter}
                expandedClauseIds={expandedClauseIds}
                onClauseToggle={handleClauseToggle}
            />
        </>
    );

    const renderFields = () => (
        <FieldsList
            scopes={data.scopes}
            searchValue={searchValue}
            expandedPolicyTypes={expandedPolicyTypes}
            onPolicyTypeToggle={handlePolicyTypeToggle}
        />
    );

    return (
        <FrontendPageContent className="flex min-h-0 flex-col overflow-auto">
            <PageHeader
                title={data.name}
                leftContent={<PageHeaderLeftContent comparison={data} />}
                actions={<PageHeaderActions comparison={data} />}
            />
            <FilterBar
                coverageFilter={coverageFilter}
                onCoverageFilterChange={setCoverageFilter}
                clauseTypeFilter={clauseTypeFilter}
                onClauseTypeFilterChange={setClauseTypeFilter}
                searchValue={searchValue}
                onSearchChange={setSearchValue}
                onExpandAll={handleExpandAll}
                onCollapseAll={handleCollapseAll}
                totalClauses={
                    new Set(
                        data.scopes.flatMap((scope) =>
                            scope.scope_clauses.map((sc) => sc.clause.id)
                        )
                    ).size
                }
                expandedCount={expandedClauseIds.size}
                totalPolicyExtractions={
                    new Set(
                        data.scopes.flatMap(
                            (scope) =>
                                scope.extracted_fields_v2
                                    ?.filter(
                                        (fg) => fg.scalar_fields.length > 0
                                    )
                                    .map((fg) => fg.policy_type) ?? []
                        )
                    ).size
                }
                expandedPolicyExtractionsCount={expandedPolicyTypes.size}
                availableClauseTypes={getUniqueClauseTypes()}
                viewMode={viewMode}
                onViewModeChange={setViewMode}
                hasFields={comparisonHasFields(data)}
            />

            {/* Clauses */}
            {viewMode === "clauses" && renderClauses()}

            {/* Fields */}
            {viewMode === "fields" && renderFields()}
        </FrontendPageContent>
    );
};

export default Chart;
